import { supabase } from "../../supabase/supabase";

// Interface for webhook data structure
export interface Webhook {
  id: string;
  url: string;
  description?: string;
  version?: number;
  channels?: string[];
  filterTypes?: string[];
  disabled?: boolean;
  rateLimit?: number;
  createdAt: string;
  updatedAt: string;
}

// Interface for webhook creation/update data
export interface WebhookInput {
  url: string;
  description?: string;
  version?: number;
  channels?: string[];
  filterTypes?: string[];
  disabled?: boolean;
  rateLimit?: number;
  secret?: string;
}

export const webhookService = {
  /**
   * Fetches all webhooks for the current organization
   * @param organizationId The organization ID
   * @returns A list of webhooks and the Svix application ID
   */
  async getWebhooks(organizationId: string): Promise<{
    webhooks: Webhook[];
    appId: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "get_svix_webhooks",
        {
          body: { organizationId },
        }
      );

      if (error) {
        console.error("Error fetching webhooks:", error);
        if (error.message && error.message.includes("CORS")) {
          throw new Error(
            "CORS error: This may be due to the function not being deployed or CORS settings not configured. Check the Supabase Edge Function deployment."
          );
        }
        throw error;
      }

      return data || { webhooks: [], appId: "" };
    } catch (error: any) {
      console.error("Error in getWebhooks service:", error);
      // Provide a better error message if it's a network error
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        throw new Error(
          "Network error: Could not connect to the Supabase Edge Function. Ensure the function is deployed and Supabase is running."
        );
      }
      throw error;
    }
  },

  /**
   * Creates a new webhook
   * @param organizationId The organization ID
   * @param webhookData The webhook data to create
   * @returns The created webhook
   */
  async createWebhook(organizationId: string, webhookData: WebhookInput): Promise<Webhook> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "manage_svix_webhook",
        {
          body: {
            organizationId,
            action: "create",
            webhookData,
          },
        }
      );

      if (error) {
        console.error("Error creating webhook:", error);
        if (error.message && error.message.includes("CORS")) {
          throw new Error(
            "CORS error: This may be due to the function not being deployed or CORS settings not configured. Check the Supabase Edge Function deployment."
          );
        }
        throw error;
      }

      return data.result;
    } catch (error: any) {
      console.error("Error in createWebhook service:", error);
      // Provide a better error message if it's a network error
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        throw new Error(
          "Network error: Could not connect to the Supabase Edge Function. Ensure the function is deployed and Supabase is running."
        );
      }
      throw error;
    }
  },

  /**
   * Updates an existing webhook
   * @param organizationId The organization ID
   * @param endpointId The endpoint ID to update
   * @param webhookData The webhook data to update
   * @returns The updated webhook
   */
  async updateWebhook(
    organizationId: string,
    endpointId: string,
    webhookData: WebhookInput
  ): Promise<Webhook> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "manage_svix_webhook",
        {
          body: {
            organizationId,
            action: "update",
            endpointId,
            webhookData,
          },
        }
      );

      if (error) {
        console.error("Error updating webhook:", error);
        if (error.message && error.message.includes("CORS")) {
          throw new Error(
            "CORS error: This may be due to the function not being deployed or CORS settings not configured. Check the Supabase Edge Function deployment."
          );
        }
        throw error;
      }

      return data.result;
    } catch (error: any) {
      console.error("Error in updateWebhook service:", error);
      // Provide a better error message if it's a network error
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        throw new Error(
          "Network error: Could not connect to the Supabase Edge Function. Ensure the function is deployed and Supabase is running."
        );
      }
      throw error;
    }
  },

  /**
   * Deletes a webhook
   * @param organizationId The organization ID
   * @param endpointId The endpoint ID to delete
   * @returns A success status
   */
  async deleteWebhook(
    organizationId: string,
    endpointId: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "manage_svix_webhook",
        {
          body: {
            organizationId,
            action: "delete",
            endpointId,
          },
        }
      );

      if (error) {
        console.error("Error deleting webhook:", error);
        if (error.message && error.message.includes("CORS")) {
          throw new Error(
            "CORS error: This may be due to the function not being deployed or CORS settings not configured. Check the Supabase Edge Function deployment."
          );
        }
        throw error;
      }

      return data.result.success;
    } catch (error: any) {
      console.error("Error in deleteWebhook service:", error);
      // Provide a better error message if it's a network error
      if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
        throw new Error(
          "Network error: Could not connect to the Supabase Edge Function. Ensure the function is deployed and Supabase is running."
        );
      }
      throw error;
    }
  },
};